import { useEffect, useRef, useState } from 'react';
import type { SeatJoinToast as SeatJoinToastData } from '../../hooks/useWheelRoomStream';

/** How long a "X took Y's seat" toast stays on screen (matches the
 *  CSS keyframe duration below). */
const TOAST_LIFETIME_MS = 3500;

export interface SeatJoinToastProps {
  /** The hook's seat-join toast buffer (newest last). The local
   *  player's own join is already filtered out upstream, so every
   *  entry here is another human arriving. */
  toasts: ReadonlyArray<SeatJoinToastData>;
}

/**
 * Room-level toast — {@code "{player} took {bot}'s seat"} — shown when
 * another human joins the table (Social). Renders the newest unexpired
 * entry from the hook's buffer as a top-anchored banner that slides in,
 * holds, then fades. One at a time: joins are infrequent enough that a
 * fresh arrival simply replaces whatever's showing.
 *
 * <p>Deliberately the simple version of the GDD §4.2 seat-replacement
 * ceremony — producer call 2026-05-31: a four-word toast reads clearer
 * than a multi-beat character animation. Fires only on human JOIN (never
 * leave / bot transitions), in both private and public rooms.
 */
export function SeatJoinToast({ toasts }: SeatJoinToastProps) {
  const [shown, setShown] = useState<SeatJoinToastData | null>(null);
  const lastClientId = useRef<number>(0);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (toasts.length === 0) return;
    const latest = toasts[toasts.length - 1];
    if (!latest) return;
    if (latest.clientId <= lastClientId.current) return; // already handled
    lastClientId.current = latest.clientId;
    // Guard against a stale buffer entry (e.g. a remount) surfacing late.
    if (Date.now() - latest.emittedAtMs > TOAST_LIFETIME_MS) return;
    setShown(latest);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setShown(null), TOAST_LIFETIME_MS);
  }, [toasts]);

  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    [],
  );

  if (!shown) return null;

  const who =
    shown.playerName && shown.playerName.trim().length > 0
      ? shown.playerName.trim()
      : 'Someone';

  return (
    <>
      <style>{`
        @keyframes seatJoinToastIn {
          0%   { opacity: 0; transform: translate(-50%, -12px); }
          12%  { opacity: 1; transform: translate(-50%, 0); }
          88%  { opacity: 1; transform: translate(-50%, 0); }
          100% { opacity: 0; transform: translate(-50%, -12px); }
        }
      `}</style>
      <div
        role="status"
        aria-live="polite"
        style={{
          position: 'fixed',
          top: 96,
          left: '50%',
          zIndex: 60,
          padding: '9px 16px',
          borderRadius: 999,
          maxWidth: '88%',
          background:
            'linear-gradient(180deg, rgba(46,26,82,0.96) 0%, rgba(28,14,54,0.96) 100%)',
          border: '1.5px solid rgba(255,228,138,0.45)',
          color: '#fff',
          fontFamily: '"Fredoka", system-ui',
          fontSize: 13.5,
          fontWeight: 600,
          letterSpacing: 0.2,
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          pointerEvents: 'none',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          animation: `seatJoinToastIn ${TOAST_LIFETIME_MS}ms ease-out forwards`,
        }}
      >
        <span style={{ color: '#ffe48a', fontWeight: 800 }}>{who}</span>
        {' took '}
        <span style={{ fontWeight: 800 }}>{shown.botName}</span>
        {'’s seat'}
      </div>
    </>
  );
}
